import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, amber, useTheme } from '../tokens';
import { useTranslation } from '../i18n';
import { getPanelTree, LEGACY_SECTION_MAP } from '../data/panelCatalog';

const purple = '#7C3AED';
const purpleLight = '#EDE9FE';
const teal = '#0D9488';
// const tealLight = '#CCFBF1';

// Statement type accent colors
const ST_COLORS = {
  balance_sheet: '#3B82F6',
  income_statement: '#10B981',
  cash_flow: '#8B5CF6',
  adjustments: '#F59E0B',
};

function getTaskStats(tasks) {
  const total = tasks.length;
  const done = tasks.filter(t => t.done).length;
  const reviewed = tasks.filter(t => t.reviewed).length;
  const blocked = tasks.filter(t => t.blocked).length;
  return { total, done, reviewed, blocked };
}

function getStatusDot(stats) {
  if (stats.total === 0) return '#CBD5E1';
  if (stats.reviewed === stats.total) return green;
  if (stats.done === stats.total) return teal;
  if (stats.blocked === stats.total) return purple;
  if (stats.done > 0) return amber;
  return '#CBD5E1';
}

export default function PanelSidebar({
  tasks,
  activePanelIds,
  selectedPanelId,
  onSelectPanel,
  collapsed,
  onToggleCollapse,
  glMappings,
}) {
  const t = useTheme();
  const tr = useTranslation();
  const [legendVisible, setLegendVisible] = useState(false);
  const [expandedStatements, setExpandedStatements] = useState({
    balance_sheet: true,
    income_statement: false,
    cash_flow: false,
    adjustments: true,
  });
  const [expandedCategories, setExpandedCategories] = useState({});

  // Group tasks by panelId using the legacy section map
  const tasksByPanel = useMemo(() => {
    const map = {};
    (tasks || []).forEach(task => {
      const panelId = task.panelId || LEGACY_SECTION_MAP[task.section];
      if (panelId) {
        if (!map[panelId]) map[panelId] = [];
        map[panelId].push(task);
      }
    });
    return map;
  }, [tasks]);

  // GL balances by panel from imported TB
  const balanceByPanel = useMemo(() => {
    if (!glMappings || !glMappings.accounts) return {};
    const map = {};
    glMappings.accounts.forEach(acc => {
      if (acc.panelId) {
        if (!map[acc.panelId]) map[acc.panelId] = { balance: 0, count: 0 };
        map[acc.panelId].balance += (acc.balance || 0);
        map[acc.panelId].count++;
      }
    });
    return map;
  }, [glMappings]);

  // Build the panel tree
  const tree = useMemo(() => getPanelTree(activePanelIds), [activePanelIds]);

  const toggleStatement = (id) => {
    setExpandedStatements(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleCategory = (id) => {
    setExpandedCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Compact collapsed sidebar
  if (collapsed) {
    return (
      <div style={{
        width: 44, flexShrink: 0, borderRight: `1px solid ${t.border}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8,
        background: t.bgSurface || '#F8FAFC',
      }}>
        <button
          onClick={onToggleCollapse}
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: 14, color: t.textMuted, padding: 6, borderRadius: 4,
          }}
          title="Expand sidebar"
        >
          {'\u25B6'}
        </button>
        {tree.map(st => {
          const stTasks = Object.entries(tasksByPanel)
            .filter(([pid]) => st.categories.some(c => c.panels.some(p => p.id === pid)))
            .flatMap(([, tks]) => tks);
          const stats = getTaskStats(stTasks);
          return (
            <div
              key={st.id}
              title={st.label}
              style={{
                width: 24, height: 24, borderRadius: 4, marginTop: 6,
                background: ST_COLORS[st.id] + '18',
                border: `1.5px solid ${ST_COLORS[st.id]}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, fontWeight: 700, fontFamily: mono, color: ST_COLORS[st.id],
                cursor: 'pointer',
              }}
              onClick={() => { onToggleCollapse(); toggleStatement(st.id); }}
            >
              {stats.reviewed}/{stats.total}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{
      width: 260, flexShrink: 0, borderRight: `1px solid ${t.border}`,
      display: 'flex', flexDirection: 'column',
      background: t.bgSurface || '#F8FAFC',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 12px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
          <span style={{ fontSize: 11, fontWeight: 700, fontFamily: sans, color: t.textPrimary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Panels
          </span>
          {/* Legend trigger */}
          <span
            onMouseEnter={() => setLegendVisible(true)}
            onMouseLeave={() => setLegendVisible(false)}
            style={{ fontSize: 10, color: t.textMuted, cursor: 'default', userSelect: 'none', lineHeight: 1 }}
            title={tr('sidebar.statusLegend')}
          >
            &#9432;
          </span>
          {/* Legend popover */}
          {legendVisible && (
            <div style={{
              position: 'absolute', top: 20, left: 0, zIndex: 100,
              background: t.bgCard, border: `1px solid ${t.border}`,
              borderRadius: 8, padding: '10px 12px', width: 200,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, marginBottom: 8 }}>
                {tr('sidebar.statusLegend')}
              </div>
              {[
                { color: '#CBD5E1', label: tr('sidebar.status.notStarted') },
                { color: amber,     label: tr('sidebar.status.inProgress') },
                { color: teal,      label: tr('sidebar.status.done') },
                { color: green,     label: tr('sidebar.status.reviewed') },
                { color: purple,    label: tr('sidebar.status.blocked') },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontFamily: sans, color: t.textSecondary }}>{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onToggleCollapse}
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: 12, color: t.textMuted, padding: '2px 4px', borderRadius: 4,
          }}
          title="Collapse sidebar"
        >
          {'\u25C0'}
        </button>
      </div>

      {/* Tree */}
      <div style={{ padding: '6px 0', flex: 1 }}>
        {tree.map(st => {
          const isExpanded = expandedStatements[st.id] !== false;
          // Aggregate stats for this statement type
          const stPanelIds = st.categories.flatMap(c => c.panels.map(p => p.id));
          const stTasks = stPanelIds.flatMap(pid => tasksByPanel[pid] || []);
          const stStats = getTaskStats(stTasks);

          return (
            <div key={st.id}>
              {/* Statement type header */}
              <div
                onClick={() => toggleStatement(st.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 12px', cursor: 'pointer',
                  borderLeft: `3px solid ${ST_COLORS[st.id]}`,
                }}
              >
                <span style={{
                  fontSize: 10, color: t.textMuted, transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s', display: 'inline-block',
                }}>{'\u25B6'}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: ST_COLORS[st.id],
                  fontFamily: sans, flex: 1,
                }}>
                  {st.label}
                </span>
                <span style={{
                  fontSize: 9, fontFamily: mono, color: t.textFaint,
                  background: stStats.reviewed === stStats.total && stStats.total > 0 ? greenLight : 'transparent',
                  padding: '1px 5px', borderRadius: 8,
                }}>
                  {stStats.total > 0 ? `${stStats.reviewed}/${stStats.total}` : ''}
                </span>
              </div>

              {/* Categories */}
              {isExpanded && st.categories.map(cat => {
                const catExpanded = expandedCategories[cat.id] !== false;
                const catPanelIds = cat.panels.map(p => p.id);
                const catTasks = catPanelIds.flatMap(pid => tasksByPanel[pid] || []);
                const catStats = getTaskStats(catTasks);

                return (
                  <div key={cat.id}>
                    {/* Category header */}
                    <div
                      onClick={() => toggleCategory(cat.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 12px 5px 24px', cursor: 'pointer',
                      }}
                    >
                      <span style={{
                        fontSize: 8, color: t.textFaint, transform: catExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s', display: 'inline-block',
                      }}>{'\u25B6'}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: t.textSecondary,
                        fontFamily: sans, flex: 1, textTransform: 'uppercase', letterSpacing: 0.3,
                      }}>
                        {cat.label}
                      </span>
                      {catStats.total > 0 && (
                        <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
                          {catStats.reviewed}/{catStats.total}
                        </span>
                      )}
                    </div>

                    {/* Panels */}
                    {catExpanded && cat.panels.map(panel => {
                      const panelTasks = tasksByPanel[panel.id] || [];
                      const stats = getTaskStats(panelTasks);
                      const dotColor = getStatusDot(stats);
                      const isSelected = selectedPanelId === panel.id;

                      return (
                        <div
                          key={panel.id}
                          onClick={() => onSelectPanel(panel.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '5px 10px 5px 38px', cursor: 'pointer',
                            background: isSelected ? green + '0C' : 'transparent',
                            borderRight: isSelected ? `2px solid ${green}` : '2px solid transparent',
                            transition: 'all 0.1s',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = t.hoverBg || '#F1F5F9'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          {/* Status dot */}
                          <div style={{
                            width: 7, height: 7, borderRadius: '50%',
                            background: dotColor, flexShrink: 0,
                          }} />

                          {/* Label */}
                          <span style={{
                            fontSize: 11, fontFamily: sans,
                            color: isSelected ? green : stats.total > 0 ? t.textPrimary : t.textFaint,
                            fontWeight: isSelected ? 600 : 400,
                            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {panel.label}
                          </span>

                          {/* Count */}
                          {stats.total > 0 && (
                            <span style={{
                              fontSize: 9, fontFamily: mono,
                              color: stats.reviewed === stats.total ? greenDark : t.textFaint,
                              fontWeight: stats.reviewed === stats.total ? 600 : 400,
                            }}>
                              {stats.done}/{stats.total}
                            </span>
                          )}

                          {/* GL balance from TB */}
                          {balanceByPanel[panel.id] && (
                            <span style={{
                              fontSize: 8, fontFamily: mono, fontWeight: 500,
                              color: balanceByPanel[panel.id].balance < 0 ? '#C0392B' : t.textFaint,
                            }}>
                              ${Math.abs(balanceByPanel[panel.id].balance) >= 1000
                                ? (Math.abs(balanceByPanel[panel.id].balance) / 1000).toFixed(0) + 'K'
                                : Math.abs(balanceByPanel[panel.id].balance).toFixed(0)}
                            </span>
                          )}

                          {/* Blocked badge */}
                          {stats.blocked > 0 && (
                            <span style={{
                              fontSize: 7, padding: '0px 4px', borderRadius: 6,
                              background: purpleLight, color: purple, fontWeight: 700,
                            }}>
                              {stats.blocked}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
